import React from 'react';
import { prefixObjectValues, getHtmlFromMarkdown } from '../../../utils';
import { FieldRenderProps } from '@progress/kendo-react-form';
import chevronRight from '../../../images/blue-arrow-right-pagination.svg';
import useHandlebars from '../../../hooks/use-handlebars';
import { PersonDataContext } from '../../../contexts/person-data-context';
import { PreferencesContext } from '../../../contexts/preferences-context';
import { models } from '../../../utils/cms-client';
import {
  ClaimCard,
  TitleBar,
  CardHeader,
  Description,
  NotFoundContainer,
  MediumHeader,
} from '../tw-components';

interface IntakeCardProps extends FieldRenderProps {
  keyId?: string;
  card: models.IntakeCard;
  name: string;
  formRenderProps: {
    onSubmit?: (e?: any) => void;
    allowSubmit?: boolean;
  };
}

const domIdsStatic = {
  rootNode: 'intake-card',
  radioButton: 'radio-button',
  notFound: 'not-found-card',
};

export const domIdsUnique = (prefix?: string) =>
  prefixObjectValues(prefix, domIdsStatic);

const IntakeCard: (p: IntakeCardProps) => React.ReactElement = (p) => {
  const { card, name, onChange, formRenderProps } = p;
  const { personData } = React.useContext(PersonDataContext);
  const { employerPreferences } = React.useContext(PreferencesContext);
  const domIds = domIdsUnique(p.keyId);

  const content = useHandlebars(card.elements.description.value, {
    ...personData,
    employerPreferences,
  });

  const submitTimeoutRef = React.useRef<number | null>(null);

  // track mount status to guard async work
  const isMountedRef = React.useRef(true);
  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (submitTimeoutRef.current !== null) {
        clearTimeout(submitTimeoutRef.current);
        submitTimeoutRef.current = null;
      }
    };
  }, []);

  const selectAndSubmit = React.useCallback(
    (e?: React.MouseEvent | React.KeyboardEvent) => {
      e?.preventDefault();

      onChange?.({
        target: { name, value: card.elements.path?.value },
        value: card.elements.path?.value,
      });

      // Schedule submit on the next tick so form state reflects the new value
      // Store the timeout id so we can cancel if the component unmounts
      submitTimeoutRef.current = window.setTimeout(() => {
        if (!isMountedRef.current) return;
        formRenderProps?.onSubmit?.();
      }, 0);
    },
    [onChange, card.elements.path?.value, name, formRenderProps],
  );

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      selectAndSubmit(e);
    }
  };

  if (p.name && card.elements.button_text?.value?.length) {
    const optionChecked = p.value === card?.elements?.path?.value;

    return (
      <ClaimCard
        id={domIds.rootNode}
        role="button"
        tabIndex={0}
        onClick={selectAndSubmit}
        onKeyDown={onKeyDown}
        aria-pressed={optionChecked}
        data-gtm-dl={JSON.stringify({
          attribute1: 'file a claim',
          attribute2: 'what type of claim is it',
          attribute3: 'select_claim_category',
          attribute4: card.elements.header.value,
        })}
      >
        <TitleBar>
          {card.elements.icon?.value && card.elements.icon.value.length > 0 ? (
            <div className="tw-flex tw-flex-row tw-w-full tw-items-center">
              <div className="tw-w-14 lg:tw-w-16 tw-mr-0 sm:tw-mr-3 lg:tw-mr-0">
                <img
                  src={card.elements.icon.value[0].url}
                  alt=""
                  className="tw-w-10 tw-h-10 md:tw-w-12 md:tw-h-12 "
                />
              </div>
              <CardHeader className="tw-w-5/6 sm:tw-w-11/12">
                {card.elements.header.value}
              </CardHeader>
            </div>
          ) : (
            <CardHeader>{card.elements.header.value}</CardHeader>
          )}
        </TitleBar>
        <div className="tw-flex tw-flex-row tw-w-full">
          <div className="tw-hidden sm:tw-block sm:tw-w-14 lg:tw-w-16 tw-mr-0 sm:tw-mr-3 lg:tw-mr-0"></div>
          <Description className="tw-w-full sm:tw-w-11/12">
            <div className="tw-flex tw-flex-col sm:tw-flex-row tw-items-center tw-w-full">
              <div
                className="tw-flex-1 tw-mb-0 tw-mr-0 sm:tw-mr-5"
                dangerouslySetInnerHTML={getHtmlFromMarkdown(content)}
              />
              <div className="tw-ml-auto tw-flex tw-items-center tw-gap-2 tw-self-end tw-mb-4">
                <span className="tw-text-lg tw-font-medium tw-text-unm-interactive01-400">
                  Choose
                </span>
                <img
                  src={chevronRight}
                  alt="Arrow right"
                  className="tw-h-4 tw-w-4 tw-shrink-0"
                />
              </div>
            </div>
          </Description>
        </div>
      </ClaimCard>
    );
  } else {
    return (
      <NotFoundContainer id={domIds.notFound}>
        <MediumHeader>{card.elements.header.value}</MediumHeader>
        <Description>
          <div dangerouslySetInnerHTML={getHtmlFromMarkdown(content)} />
        </Description>
      </NotFoundContainer>
    );
  }
};

export default IntakeCard;
